import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useNavigate } from 'react-router-dom';
import { Inventory, InventoryItem } from '@/types';
import { InventoryService } from '@/services/InventoryService';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { useI18n } from '@/i18n/I18nContext';
import AIChatOverlay, { ChatMessage, AI_CHAT_USE_FOOTER, AI_CHAT_FOOTER_BAR_HEIGHT } from '@/components/AIChatOverlay';
import CreditsDisplay from '@/components/CreditsDisplay';
import { AIService } from '@/services/AIService';
import GrainIcon from '@mui/icons-material/Grain';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';
import CategoryIcon from '@mui/icons-material/Category';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Get inventory-specific suggested questions for the AI chat
 */
const getSuggestedQuestionsForInventory = (inventory: Inventory): string[] => {
	const questions: string[] = [
		'What can you tell me about my inventory?',
		'Do I have everything I need to brew a specific recipe?',
		'What items are expiring soon?',
		'What should I restock?',
		'Can you suggest recipes based on my current inventory?'
	];

	if (inventory.categories.hops > 0) {
		questions.push('What can I brew with my current hops?');
	}

	if (inventory.categories.malt > 0) {
		questions.push('What styles can I brew with my malts?');
	}

	if (inventory.categories.yeast > 0) {
		questions.push('What recipes work well with my yeast strains?');
	}

	// Check for expiring items
	const now = new Date();
	const expiringItems = inventory.items.filter(item => {
		if (!item.expirationDate) return false;
		const expDate = new Date(item.expirationDate);
		const daysUntilExp = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
		return daysUntilExp >= 0 && daysUntilExp <= 30;
	});

	if (expiringItems.length > 0) {
		questions.push(`I have ${expiringItems.length} item(s) expiring soon. What should I do?`);
	}

	return questions.slice(0, 8); // Limit to 8 questions
};

const getCategoryIcon = (category: InventoryItem['category']) => {
	switch (category) {
		case 'hops':
			return <WhatshotIcon />;
		case 'malt':
			return <GrainIcon />;
		case 'yeast':
			return <LocalBarIcon />;
		case 'equipment':
			return <BuildIcon />;
		case 'additives':
			return <ScienceIcon />;
		default:
			return <CategoryIcon />;
	}
};

const getCategoryColor = (category: InventoryItem['category']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
	switch (category) {
		case 'hops':
			return 'error';
		case 'malt':
			return 'warning';
		case 'yeast':
			return 'primary';
		case 'equipment':
			return 'default';
		case 'additives':
			return 'info';
		default:
			return 'secondary';
	}
};

const InventoryPage: React.FC = () => {
	const navigate = useNavigate();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { setCreditsLoading, loadCredits } = useCredits();
	const { t } = useI18n();
	const [inventory, setInventory] = useState<Inventory | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!user) {
			navigate('/login');
			return;
		}
		loadInventory();
	}, [user]);

	const loadInventory = async () => {
		try {
			setLoading(true);
			const userId = getUserId();
			if (!userId) {
				navigate('/login');
				return;
			}
			const authHeader = getAuthHeader();
			const inv = await InventoryService.getInventory(userId, authHeader);
			setInventory(inv);
		} catch (error: any) {
			console.error('Error loading inventory:', error);
			setInventory(null);
		} finally {
			setLoading(false);
		}
	};

	const isExpiringSoon = (expirationDate?: string): boolean => {
		if (!expirationDate) return false;
		const expDate = new Date(expirationDate);
		const now = new Date();
		const daysUntilExp = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
		return daysUntilExp >= 0 && daysUntilExp <= 30;
	};

	if (loading) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	if (!inventory) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<Typography variant="h6" color="text.secondary">
					Failed to load inventory
				</Typography>
			</Container>
		);
	}

	const expiringItems = inventory.items.filter(item => isExpiringSoon(item.expirationDate));

	return (
		<Container 
			maxWidth="lg" 
			sx={{ 
				py: 4,
				...(AI_CHAT_USE_FOOTER && { pb: AI_CHAT_FOOTER_BAR_HEIGHT + 24 })
			}}
		>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
					Inventory
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Manage your brewing ingredients and supplies
				</Typography>
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					{expiringItems.length > 0 && (
						<Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.light' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<WarningIcon color="warning" />
								<Typography variant="h6" fontWeight={700}>
									Items Expiring Soon
								</Typography>
							</Box>
							<Stack spacing={1}>
								{expiringItems.map((item) => {
									const expDate = item.expirationDate ? new Date(item.expirationDate) : null;
									const daysUntilExp = expDate 
										? Math.ceil((expDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
										: null;
									return (
										<Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
											<Typography variant="body2" fontWeight={600}>
												{item.name}
											</Typography>
											{expDate && (
												<Typography variant="body2" color="text.secondary">
													Expires: {expDate.toLocaleDateString()} ({daysUntilExp} day{daysUntilExp !== 1 ? 's' : ''})
												</Typography>
											)}
										</Box>
									);
								})}
							</Stack>
						</Paper>
					)}

					<Paper sx={{ p: 3, mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
							<CategoryIcon color="primary" />
							<Typography variant="h6" fontWeight={700}>
								Inventory Summary
							</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={6} sm={4}>
								<Typography variant="caption" color="text.secondary">
									Total Items
								</Typography>
								<Typography variant="h6" fontWeight={700}>
									{inventory.totalItems}
								</Typography>
							</Grid>
							{Object.entries(inventory.categories).map(([category, count]) => {
								if (count === 0) return null;
								return (
									<Grid item xs={6} sm={4} key={category}>
										<Typography variant="caption" color="text.secondary">
											{category.charAt(0).toUpperCase() + category.slice(1)}
										</Typography>
										<Typography variant="h6" fontWeight={700}>
											{count}
										</Typography>
									</Grid>
								);
							})}
						</Grid>
					</Paper>

					<Paper sx={{ p: 3 }}>
						<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
							All Items
						</Typography>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Category</TableCell>
										<TableCell align="right">Quantity</TableCell>
										<TableCell>Expiration</TableCell>
										<TableCell>Notes</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{inventory.items.length === 0 ? (
										<TableRow>
											<TableCell colSpan={5} align="center">
												<Typography variant="body2" color="text.secondary">
													No items in inventory
												</Typography>
											</TableCell>
										</TableRow>
									) : (
										inventory.items.map((item) => (
											<TableRow key={item.id}>
												<TableCell>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														{getCategoryIcon(item.category)}
														<Typography variant="body2" fontWeight={600}>
															{item.name}
														</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Chip
														label={item.category}
														size="small"
														color={getCategoryColor(item.category)}
														variant="outlined"
													/>
												</TableCell>
												<TableCell align="right">
													<Typography variant="body2">
														{item.quantity} {item.unit}
													</Typography>
												</TableCell>
												<TableCell>
													{item.expirationDate ? (
														<Box>
															<Typography variant="body2">
																{new Date(item.expirationDate).toLocaleDateString()}
															</Typography>
															{isExpiringSoon(item.expirationDate) && (
																<Chip
																	label="Expiring Soon"
																	size="small"
																	color="warning"
																	variant="outlined"
																	sx={{ mt: 0.5 }}
																/>
															)}
														</Box>
													) : (
														<Typography variant="body2" color="text.secondary">
															—
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Typography variant="body2" color="text.secondary">
														{item.notes || '—'}
													</Typography>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
				</Grid>

				<Grid item xs={12} md={4}>
					<Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
						<Stack spacing={2}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<CategoryIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									Quick Stats
								</Typography>
							</Box>
							<Divider />
							<Box>
								<Typography variant="caption" color="text.secondary">
									Total Items
								</Typography>
								<Typography variant="h4" fontWeight={700}>
									{inventory.totalItems}
								</Typography>
							</Box>
							{expiringItems.length > 0 && (
								<Box>
									<Typography variant="caption" color="text.secondary">
										Expiring Soon
									</Typography>
									<Typography variant="h4" fontWeight={700} color="warning.main">
										{expiringItems.length}
									</Typography>
								</Box>
							)}
						</Stack>
					</Paper>
				</Grid>
			</Grid>

			{inventory && (
				<AIChatOverlay
					title="Inventory"
					subtitle="AI Assistant"
					historyKey={`inventory-${getUserId()}`}
					loadHistory={async () => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId || !authHeader) return [];
						const { items = [] } = await AIService.getQuestionHistory(userId, authHeader, undefined, undefined, userId);
						// Backend returns newest first; sort ascending so chat shows chronological order (oldest first).
						const sorted = [...items].sort((a, b) => {
							const ta = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime();
							const tb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt).getTime();
							return ta - tb;
						});
						const loaded: ChatMessage[] = [];
						for (const item of sorted) {
							loaded.push({
								id: item.id,
								role: 'user',
								content: item.question,
								timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
							});
							if (item.answer) {
								loaded.push({
									id: `${item.id}-reply`,
									role: 'assistant',
									content: item.answer,
									timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
								});
							}
						}
						return loaded;
					}}
					onAsk={async (question) => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId) throw new Error('Please log in again.');
						setCreditsLoading(true);
						try {
							const response: any = await AIService.askQuestionAboutInventory(userId, question, inventory, authHeader);
							// Handle both direct { answer } and API Gateway-wrapped { body: string } shapes
							let answer: string | undefined = response?.answer;
							if (typeof answer !== 'string' && response?.body) {
								const parsed = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
								answer = parsed?.answer;
							}
							if (typeof answer !== 'string') throw new Error('Invalid response from AI');
							return answer;
						} finally {
							await loadCredits();
							setCreditsLoading(false);
						}
					}}
					suggestedQuestions={getSuggestedQuestionsForInventory(inventory)}
					onLoginAgain={() => navigate('/login')}
					headerExtra={<CreditsDisplay variant="chip" size="small" />}
				/>
			)}
		</Container>
	);
};

export default InventoryPage;
